import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxRBACService } from "app/bfx-common/services/bfx-rbac.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import {
  HedgeOrderDataPrimaryLookupService,
  BfxBlotterAction,
  BfxBlotterContextMenuItem,
  ContextMenuItemState,
  Roles,
  BmarkFixingData,
  BmarkFixingType,
  ActiveUserProfilePromiseService,
  ActiveUser,
  TraderGroupLookupService,
  OrderPauseState,
  BfxBlotterOrdersContextMenuState,
} from "../../../../../shared/cubiq-services";
import {
  BfxBaseBlotterContextMenuService,
  BfxBlotterActionEvent,
} from "../../../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { OEActionsService } from "app/order-entry/services/oe-actions.service";
import { BmarkHedgeOrdersBlotterComponent } from "../bmark-hedge-orders-blotter.component";
import { BmarkFixingsManagerService } from "../../../services/bmark-fixings-manager.service";
import { BfxBaseBlotter } from "app/bfx-base-blotter/bfx-base-blotter";
import { LoggerService } from "@bfx/logger";
import { BmarkSummaryActionSubject } from "../../../bmark-summary-action-subject.module";
import { BfxBlotterOrdersContextMenuFillNowItem } from "shared/cubiq-services/model/contextmenu/bfx-blotter-orders/BfxBlotterOrdersContextMenuFillNowItem";
import { throttle } from "rxjs";
const logger = LoggerService.for(__dirname);

export class BmarkHedgeOrdersContextMenuItem extends BfxBlotterContextMenuItem<any> {
  public state: BmarkHedgeOrdersContextMenuItemState = new BmarkHedgeOrdersContextMenuItemState();

  constructor(
    public actionId: BfxBlotterAction,
    title: string = "",
    public iconClass: string = " ",
    public iconClassRight?: string,
  ) {
    super(
      actionId,
      title,
      () => {
        return;
      },
      () => {
        return;
      },
    );

    this.state.title = title;
  }
}

export class BmarkHedgeOrdersContextMenuItemState extends ContextMenuItemState {
  hasExternal: boolean = false;

  constructor(
    count: number = 0,
    itemIndex: number = 0,
    available: boolean = false,
    enabled: boolean = false,
    title: string = "",
  ) {
    super(count, itemIndex, available, enabled, title);
  }
}

export class BfxBlotterBmarkHedgeOrdersDialogActionEvent extends BfxBlotterActionEvent {
  constructor(
    action: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds: Array<string>,
    selectedItems: Array<any>,
    public selectedBenchmarksHedgeOrder: Array<any>, // NOTE: TradDeal was previous, and should be some type of BenchmarkOrder I guess
    itemState?: BmarkHedgeOrdersContextMenuItemState,
    hide?: boolean,
    menuBoundingHedgeRect?: any,
    actionEvent?: any,
  ) {
    super(
      action,
      ladderBlotter,
      selectedIds,
      selectedItems,
      itemState,
      hide,
      menuBoundingHedgeRect,
      actionEvent,
    );
  }
}
@Injectable()
export class BmarkHedgeOrdersContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  public menuItems: Array<BmarkHedgeOrdersContextMenuItem>;
  public selectedBenchmarksHedgeOrders: Array<CubiqTS.OrderDataImpl> = [];
  protected permissions = {
    canConfirmTrade: null,
    canCancelTrade: null,
    canAddFillComment: null,
    canViewDetails: true,
    canViewTradeDetails: true,
    canViewAuditLog: true,
    canExportTrade: true,
    canFillNow: false,
    canModifyOrder: false,
    canCancelBMHedge: false,
    // canCancelCrossClient: false,
    canUnrestrictedCancelModify: false,
    canAccept: false,
    canReject: false,
    isTraderUnrestricted: false,
    canRiskRoute: false,
    canRiskRouteFromDealt: false,
    canPause: false,
    canResume: false,
    canChangeInflightStatus: false,
  };
  protected activeUser: ActiveUser;
  protected acceptItem: BmarkHedgeOrdersContextMenuItem;
  protected rejectItem: BmarkHedgeOrdersContextMenuItem;

  protected pauseMenuItem: BmarkHedgeOrdersContextMenuItem;
  protected resumeMenuItem: BmarkHedgeOrdersContextMenuItem;

  protected fillNowItem: BfxBlotterOrdersContextMenuFillNowItem;

  constructor(
    private dataPromiseLookupService: HedgeOrderDataPrimaryLookupService,
    private traderGroupService: TraderGroupLookupService,
    private rbacService: BfxRBACService,
    private userProfileService: BfxUserProfileService,
    private oeActions: OEActionsService,
    private activeUserService: ActiveUserProfilePromiseService,
    private fixingService: BmarkFixingsManagerService,
    actionSubject: BmarkSummaryActionSubject,
  ) {
    super(actionSubject);
    this.initializePermissions();
    this.fillNowItem = new BfxBlotterOrdersContextMenuFillNowItem();
  }

  LOG_PREFIX: string = "[BfxBmarkHedgeOrdersBlotterContextMenuService] ";
  TRACE_LOGGING: boolean = true;

  private traceLog(logString: string) {
    if (this.TRACE_LOGGING) logger.out.info(this.LOG_PREFIX + logString);
  }

  private initializePermissions() {
    // static permissions

    let promises: Promise<any>[] = [
      this.activeUserService.getActiveUserData(),
      this.rbacService.hasRole(Roles.DEAL_ACK),
      this.rbacService.hasRole(Roles.DEAL_CANCEL),
      this.rbacService.hasRole(Roles.ADD_FILL_COMMENT),
      this.rbacService.hasRole(Roles.ORDER_MODIFY),
      this.rbacService.hasRole(Roles.ORDER_UNRESTRICTED_CANCEL_AND_MODIFY),
      this.rbacService.hasRole(Roles.ORDER_CANCEL_BM_HEDGE),
      this.rbacService.hasRole(Roles.DI_TRADER_ACCEPT),
      this.rbacService.hasRole(Roles.DI_TRADER_REJECT),
      this.rbacService.hasRole(Roles.DI_TRADER_UNRESTRICTED),
      this.rbacService.hasRole(Roles.ORDER_BENCHMARK_MANUAL_RISK_ROUTE),
      this.rbacService.hasRole(Roles.ORDER_BENCHMARK_MANUAL_RISK_ROUTE_FROM_DEALT),
      this.rbacService.hasRole(Roles.ROLE_ORDER_PAUSE_RESUME),
      this.rbacService.hasRole(Roles.ROLE_ORDER_ALGO_INFLIGHT),
      this.rbacService.hasRole(Roles.ROLE_ORDER_EXPORT),
      this.rbacService.hasRole(Roles.ROLE_ORDER_ALGO_FILL_NOW),
    ];

    // dynamic permissions
    Promise.all(promises).then(
      ([
        activeUser,
        canConfirmTrade,
        canCancelTrade,
        canAddFillComment,
        canModifyOrder,
        canCancelUnrestricted,
        canCancelBMHedge,
        canAccept,
        canReject,
        isTraderUnrestricted,
        canRiskRoute,
        canRiskRoutFromDealt,
        canPauseResume,
        canChangeInflightStatus,
        canExportTrade,
        canFillNow,
      ]) => {
        this.activeUser = activeUser;
        this.permissions.canConfirmTrade = canConfirmTrade;
        this.permissions.canCancelTrade = canCancelTrade;
        this.permissions.canAddFillComment = canAddFillComment;
        // TODO - discuss role superset wtih product
        this.permissions.canModifyOrder = canModifyOrder;

        if (canCancelUnrestricted) {
          this.permissions.canUnrestrictedCancelModify = true;
          // implied by ordinal nature of role // role superset -dc
          // this.permissions.canCancelCrossClient = true;
          // implied by ordinal nature of role // role superset -dc
          this.permissions.canCancelBMHedge = true;
          // Per OMS-26609 Cancel Cross doesn't apply to hedge orders -dc
          // } else if (canCancelCross) {
          //   this.permissions.canUnrestrictedCancelModify = false;
          //   this.permissions.canCancelCrossClient = true;
          //   // implied by ordinal nature of role // role superset -dc
          //   this.permissions.canCancelBMHedge = true;
        } else if (canCancelBMHedge) {
          this.permissions.canUnrestrictedCancelModify = false;
          // this.permissions.canCancelCrossClient = true;
          this.permissions.canCancelBMHedge = true;
        }

        this.permissions.canAccept = canAccept;
        this.permissions.canReject = canReject;
        this.permissions.isTraderUnrestricted = isTraderUnrestricted;

        this.permissions.canRiskRoute = canRiskRoute;
        this.permissions.canRiskRouteFromDealt = canRiskRoutFromDealt;
        this.permissions.canChangeInflightStatus = canChangeInflightStatus;
        this.permissions.canExportTrade = canExportTrade;
        this.permissions.canFillNow = canFillNow;
        this.initMenuItems();
      },
    );
  }

  /**
   *  // can perform CANCEL
   *       States with comments are allowed for cancel
   *       OrderDealState.ACTIVE, //STP In Progress - disable
   *       OrderDealState.FILL_ERROR, // STP Fill Error
   *       OrderDealState.COMPLETED, //  STP Confirmed
   *       OrderDealState.CANCELLED,
   *       OrderDealState.ACK_TIME_OUT, //STP Time Out
   *       OrderDealState.NAK, // STP NAK
   *       OrderDealState.DONE_NO_STP, // STP Disabled
   *       OrderDealState.STP_HOLD // Hold STP
   */

  protected getEnabledActions(
    items: Array<CubiqTS.OrderDataImpl>,
    selectedOrderIds: Array<string>,
    blotter?: any,
  ): Array<BfxBlotterAction> {
    let ordersCount: number = items.length;
    let enabledActionIds: Array<BfxBlotterAction> = [];
    let inItemsSalesGroupIds: boolean = true;
    let inItemsTraderGroups: boolean = true;
    let isAcceptEnabled: boolean = true;
    let isRejectEnabled: boolean = true;
    let hasExternal: boolean = false;
    let hasInternal: boolean = false;
    let areAllCancelPartFilled: boolean = true;

    let isPauseEnabled: boolean = false;
    let isResumeEnabled: boolean = false;
    let anyReasonNotToEnablePauseResume = false; // anything in the set
    let anyReasonNotToEnableUnlock = false;

    let disableInflightCheckEnabled: boolean = false;
    let enableInflightCheckEnabled: boolean = false;
    let anyNonAlgoOrders: boolean = false;

    items.forEach((item) => {
      if (null != item) {
        if (!this.activeUser.salesGroupsIdSet.has(item.salesGroupId.toString()))
          inItemsSalesGroupIds = false;

        if (!this.activeUser.tradingGroupsIdSet.has(item.deskId.toString()))
          inItemsTraderGroups = false;
      }

      if (null == item.pauseState || item.pauseState == CubiqTS.PauseState.NOT_PAUSED) {
        isPauseEnabled = true;
      } else if (item.pauseState == CubiqTS.PauseState.PAUSED) {
        isResumeEnabled = true;
      }

      if (
        null != item.orderState &&
        !(CubiqTS.OrderState.ACTIVE == item.orderState) &&
        !(CubiqTS.OrderState.PENDING == item.orderState)
      ) {
        this.traceLog(
          "no Pause/Resume because of order [" + item.orderId + "] state [" + item.orderState + "]",
        );
        anyReasonNotToEnablePauseResume = true;
      }
      if (null != item.orderType && !(CubiqTS.OrderType.BMALGO == item.orderType)) {
        this.traceLog("no Pause/Resume because of order [" + item.orderId + "] not BMALGO");
        anyReasonNotToEnablePauseResume = true;
      }

      if (item.lockUserId && item.lockType && item.lockType != null) {
        this.traceLog(
          "no Pause/Resume on order [" +
            item.orderId +
            "] as lock held by [" +
            item.lockUserId +
            "]",
        );
        anyReasonNotToEnablePauseResume = true;
      }
      anyReasonNotToEnableUnlock =
        anyReasonNotToEnableUnlock || this.isThereAnyReasonNotToEnableUnlockOn(item);

      if (
        item.orderType === CubiqTS.OrderType.BMALGO &&
        (item.orderState === CubiqTS.OrderState.ACTIVE ||
          item.orderState === CubiqTS.OrderState.PENDING)
      ) {
        if (item.inflightInclusionState === CubiqTS.InflightInclusionState.ENABLED) {
          disableInflightCheckEnabled = true;
        } else if (item.inflightInclusionState === CubiqTS.InflightInclusionState.DISABLED) {
          enableInflightCheckEnabled = true;
        }
      } else {
        anyNonAlgoOrders = true;
      }
    });

    // is there only one - we can;t modify a list
    // && user has ROLE_ORDER_MODIFY
    let anyReasonNotToEnableModify = true;

    if (ordersCount == 1 && this.permissions.canModifyOrder) {
      items.forEach((order) => {
        anyReasonNotToEnableModify =
          anyReasonNotToEnableModify || this.isThereAnyReasonNotToEnableModifyOn(order);
      });
    }
    if (ordersCount === 1 && !anyReasonNotToEnableModify) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_MODIFY);
    }

    if (ordersCount === 1 && !anyReasonNotToEnableUnlock) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_UNLOCK);
    }

    let confirmConditions: {
      notConfirmed: boolean;
      notInProgress: boolean;
      inSalesGroups: boolean;
      isSalesUser: boolean;
    } = {
      notConfirmed: items.length > 0,
      notInProgress: items.length > 0,
      inSalesGroups: inItemsSalesGroupIds,
      isSalesUser: this.userProfileService.isSalesUser(),
    };

    let cancelConditions: {
      notCanceled: boolean;
      inSalesGroups: boolean;
      isSalesUser: boolean;
      inTraderGroups: boolean;
      isTraderUser: boolean;
      // crossClient: boolean;
      // isInHardCutOff: boolean;
      isPostHCOExtPositionOrder: boolean;
      isPauseResumeLocked: boolean;
      isLocked: boolean;
    } = {
      notCanceled: items.length > 0,
      inSalesGroups: inItemsSalesGroupIds,
      isSalesUser: this.userProfileService.isSalesUser(),
      inTraderGroups: inItemsTraderGroups,
      isTraderUser: this.userProfileService.isTraderUser(),
      // crossClient: true,
      // isInHardCutOff: false,
      isPostHCOExtPositionOrder: false,
      isPauseResumeLocked: false,
      isLocked: false,
    };

    // can perform CONFIRM
    // items.forEach(deal => {
    //     confirmConditions.notConfirmed = confirmConditions.notConfirmed && (deal.confirmUserId === null || deal.confirmUserId === undefined);
    //     confirmConditions.notInProgress = confirmConditions.notInProgress && deal.dealStateId && deal.dealStateId !== CubiqTS.OrderDealState.ACTIVE;
    //     if (confirmConditions.inSalesGroups) {
    //         confirmConditions.inSalesGroups = confirmConditions.inSalesGroups && deal.salesGroupId && this.userProfileService.isMemberOfSalesGroup(deal.salesGroupId);
    //     }
    // });

    // if (confirmConditions.notConfirmed && confirmConditions.notInProgress && this.permissions.canConfirmTrade && (confirmConditions.notSalesUser || confirmConditions.inSalesGroups)) {
    //     enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_CONFIRM);
    // }

    // Can perform CANCEL, ACCEPT OR REJECT
    items.forEach((orderData, index) => {
      let traderGroup = this.traderGroupService.getTraderGroup(orderData.deskId);

      cancelConditions.notCanceled =
        (cancelConditions.notCanceled && orderData.orderState == CubiqTS.OrderState.ACTIVE) ||
        orderData.orderState == CubiqTS.OrderState.PENDING ||
        orderData.orderState == CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED;

      if (cancelConditions.inSalesGroups) {
        cancelConditions.inSalesGroups =
          cancelConditions.inSalesGroups &&
          orderData.salesGroupId &&
          this.userProfileService.isMemberOfSalesGroup(orderData.salesGroupId.toString());
      }

      // BM Algo & Manual/Internal Position orders are eligible for cancel post-HCO -dc
      cancelConditions.isPostHCOExtPositionOrder =
        this.isPostHardCutOff(orderData) &&
        CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
        CubiqTS.BenchmarkOrderType.PositionOrder === orderData.fixingDetails.bmOrderType &&
        (traderGroup !== null ? traderGroup.isExternallyFilledFg === true : false);

      cancelConditions.isPauseResumeLocked =
        OrderPauseState.isPausePendingOrderState(orderData) ||
        OrderPauseState.isResumePendingOrderState(orderData);
      cancelConditions.isLocked = orderData.lockType != null;

      isAcceptEnabled = isAcceptEnabled && this.isAcceptEnabled(orderData);
      isRejectEnabled = isRejectEnabled && this.isRejectEnabled(orderData);
      let isExternal: boolean =
        traderGroup.isExternallyFilledFg === true &&
        (CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === orderData.orderState ||
          CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED === orderData.orderState ||
          CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === orderData.orderState ||
          OrderPauseState.isPausePendingOrderState(orderData) ||
          OrderPauseState.isResumePendingOrderState(orderData));

      hasExternal = hasExternal || isExternal;
      hasInternal = hasInternal || !isExternal;

      areAllCancelPartFilled =
        areAllCancelPartFilled &&
        (orderData.orderState == CubiqTS.OrderState.CANCELLED ||
          orderData.orderState == CubiqTS.OrderState.EXPIRED) &&
        orderData.leavesQty.isGreaterThan(0);
      if (null != traderGroup.pauseResumeFg && false == traderGroup.pauseResumeFg) {
        isPauseEnabled = false;
        isResumeEnabled = false;
      }
    });

    // No mixing of internal and external acceptance
    if (hasExternal && hasInternal) {
      isAcceptEnabled = false;
      isRejectEnabled = false;
    }

    if (ordersCount > 0 && isAcceptEnabled) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_ACCEPT_CANCEL);

      // update state for warning dialog
      this.acceptItem.state.hasExternal = hasExternal;
      this.acceptItem.state.title = hasExternal ? "Force Accept" : "Accept";
    }
    if (ordersCount > 0 && isAcceptEnabled) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_REJECT_CANCEL);

      // update state for warning dialog
      this.rejectItem.state.hasExternal = hasExternal;
      this.rejectItem.state.title = hasExternal ? "Force Reject" : "Reject";
    }

    if (
      cancelConditions.notCanceled &&
      this.permissions.canCancelBMHedge &&
      (!cancelConditions.isSalesUser ||
        cancelConditions.inSalesGroups ||
        this.permissions.canUnrestrictedCancelModify) &&
      (!cancelConditions.isTraderUser ||
        cancelConditions.inTraderGroups ||
        this.permissions.canUnrestrictedCancelModify) &&
      // cancelConditions.crossClient &&
      // !cancelConditions.isHardCutOff &&
      !cancelConditions.isPostHCOExtPositionOrder &&
      !cancelConditions.isPauseResumeLocked &&
      !cancelConditions.isLocked
    ) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_CANCEL);
    }

    // ADD_FILL_COMMENT & VIEW_DETAILS
    if (ordersCount === 1) {
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_ADD_FILL_COMMENT);
      enabledActionIds.push(BfxBlotterAction.ACTION_VIEW_DETAILS);
      enabledActionIds.push(BfxBlotterAction.ACTION_VIEW_AUDIT);
    }

    if (ordersCount !== 0) {
      enabledActionIds.push(BfxBlotterAction.ACTION_TRADE_EXPORT);
    }

    if (areAllCancelPartFilled) {
      if (blotter instanceof BmarkHedgeOrdersBlotterComponent) {
        let hedgeBlotter: BmarkHedgeOrdersBlotterComponent =
          blotter as BmarkHedgeOrdersBlotterComponent;
        let embeddedFixing: BmarkFixingData = hedgeBlotter.service.getEmbeddedFixing();

        if (
          embeddedFixing != null &&
          this.fixingService.data != null &&
          this.fixingService.data.activeFixingsList != null
        ) {
          let fixingActive: boolean =
            this.fixingService.data.activeFixingsList.find(
              (fixing: BmarkFixingData) => fixing.id == embeddedFixing.id,
            ) != null;
          if (fixingActive && this.permissions.canRiskRoute) {
            enabledActionIds.push(BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO);
            if (this.permissions.canRiskRouteFromDealt) {
              enabledActionIds.push(BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO_DEALT);
            }
          }
        }
      }
    }

    if (!anyReasonNotToEnablePauseResume) {
      if (isPauseEnabled) {
        enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_PAUSE);
      }

      if (isResumeEnabled) {
        enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_RESUME);
      }
    }

    if (!anyNonAlgoOrders && disableInflightCheckEnabled) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_DISABLE_INFLIGHT);
    }

    if (!anyNonAlgoOrders && enableInflightCheckEnabled) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_ENABLE_INFLIGHT);
    }

    if (ordersCount == 1 && this.permissions.canFillNow && this.canFillNow(items[0])) {
      enabledActionIds.push(BfxBlotterAction.ACTION_ORDER_FILL_NOW);
    }
    return enabledActionIds;
  }

  /**
   * Check if Fill Now can be executed on the order - delegates the functionality to the main
   * algo blotter menu item
   * @param order
   * @returns
   */
  private canFillNow(order: CubiqTS.OrderDataImpl): boolean {
    const menuState = new BfxBlotterOrdersContextMenuState();
    menuState.traderGroups.set(
      order.deskId.toString(),
      this.traderGroupService.getTraderGroup(order.deskId),
    );
    menuState.activeUser = this.activeUser;
    menuState.count = 1;

    const itemState = new ContextMenuItemState();

    this.fillNowItem.checkEnabled(menuState, itemState, order);
    return itemState.enabled;
  }

  public isThereAnyReasonNotToEnableModifyOn(order: CubiqTS.OrderDataImpl): boolean {
    // is it locked or not active
    // if(order.lockUserId || order.orderState !== CubiqTS.OrderState.ACTIVE) {
    //   // I'll have a no please bob
    //   this.traceLog("order [" + order.orderId +"] no modify because of lock [" + order.lockUserId + "] orderState[" + order.orderState +"]");
    //   return true;
    // } else {

    //   // is its fixing in HCO
    //   let tempFixing = new BmarkFixingData();
    //   let fixingId = tempFixing.generate(order.fixingSourceCd, order.fixingDate, null);

    //   // try active fixings - recent fixings have passed so orders should not be editable
    //   let foundFixingData :BmarkFixingData = null;
    //   this.dataPromiseLookupService.getBmarkFixingData(fixingId, BmarkFixingType.ACTIVE)
    //     .then((found: BmarkFixingData)=>{
    //       foundFixingData = found;
    //       if(foundFixingData && foundFixingData.state && foundFixingData.state === CubiqTS.FixingState.HARD_CUTOFF) {
    //         this.traceLog("order [" + order.orderId +"] no modify because of fixing [" + fixingId+ "] in HCO ");
    //         return true;
    //       }
    //       return false;
    //   });
    // }

    // hedges don't support modify at presents -dc
    return true;
  }

  public isPostHardCutOff(order): boolean {
    // is its fixing in HCO
    let fixingId = BmarkFixingData.id(
      order.fixingSourceCd,
      order.fixingDate,
      BmarkFixingType.ACTIVE,
    );

    // try active fixings - recent fixings have passed so orders should not be editable
    let foundFixingData: BmarkFixingData =
      this.dataPromiseLookupService.getActiveFixingData(fixingId);
    if (foundFixingData != null) {
      if (
        foundFixingData &&
        foundFixingData.state &&
        (foundFixingData.state === CubiqTS.FixingState.FIXING_HIT ||
          foundFixingData.state === CubiqTS.FixingState.COMPLETED)
      ) {
        this.traceLog(
          "order [" + order.orderId + "] no modify because of fixing [" + fixingId + "] post HCO ",
        );
        return true;
      }
    }
    return false;
  }

  public isInHardCutOff(order): boolean {
    // is its fixing in HCO
    let fixingId = BmarkFixingData.id(
      order.fixingSourceCd,
      order.fixingDate,
      BmarkFixingType.ACTIVE,
    );

    // try active fixings - recent fixings have passed so orders should not be editable
    let foundFixingData: BmarkFixingData =
      this.dataPromiseLookupService.getActiveFixingData(fixingId);
    if (foundFixingData != null) {
      if (
        foundFixingData &&
        foundFixingData.state &&
        foundFixingData.state === CubiqTS.FixingState.HARD_CUTOFF
      ) {
        this.traceLog(
          "order [" + order.orderId + "] no modify because of fixing [" + fixingId + "] in HCO ",
        );
        return true;
      }
    }
    return false;
  }

  public isInAcceptance(order: CubiqTS.OrderDataImpl): boolean {
    let inAcceptance =
      CubiqTS.OrderState.ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED === order.orderState ||
      CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED === order.orderState ||
      OrderPauseState.isPausePendingOrderState(order) ||
      OrderPauseState.isResumePendingOrderState(order); // ||
    // CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED === order.orderState

    return inAcceptance;
  }

  public isAcceptEnabled(order: CubiqTS.OrderDataImpl): boolean {
    let inAcceptance = this.isInAcceptance(order);
    let isOwned = this.activeUser.tradingGroupsIdSet.has(order.deskId.toString());
    let isTraderUnrestricted = this.permissions.isTraderUnrestricted;

    return inAcceptance && ((isOwned && this.permissions.canAccept) || isTraderUnrestricted);
  }

  public isRejectEnabled(order: CubiqTS.OrderDataImpl): boolean {
    let inAcceptance = this.isInAcceptance(order);
    let isOwned = this.activeUser.tradingGroupsIdSet.has(order.deskId.toString());
    let isTraderUnrestricted = this.permissions.isTraderUnrestricted;

    return inAcceptance && ((isOwned && this.permissions.canReject) || isTraderUnrestricted);
  }

  public isThereAnyReasonNotToEnableUnlockOn(order: CubiqTS.OrderDataImpl): boolean {
    // if it locked (SI, FILL or MODIFY) and NOT (in Aceptance)
    // then it can be locked
    if (
      order.lockUserId &&
      order.lockType &&
      [
        CubiqTS.LockType.SI_LOCK,
        CubiqTS.LockType.FILL_LOCK,
        CubiqTS.LockType.FILL_NOW_LOCK,
        CubiqTS.LockType.MODIFY_LOCK,
      ].includes(order.lockType) &&
      order.orderState &&
      ![CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED, CubiqTS.OrderState.ACCEPTANCE_NEEDED].includes(
        order.orderState,
      )
    ) {
      return false;
    }
    return true;
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingHedgeRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingHedgeRect,
      actionEvent,
    );

    this.actionSubject.next(event);
  }

  protected getActionDecorators(items: any[]) {
    // nothing to add
  }
  protected prepareActions(ladderBlotter: any): void {
    // nothing to add
  }
  public getMenuItems(): any[] {
    if (!this.menuItems) {
      this.initMenuItems();
    }
    return this.menuItems;
  }

  public buildMenu(
    blotter: BfxBaseBlotter,
    pageX: number,
    pageY: number,
    selectedIds: string[],
    selectedItems?: object[],
  ): void {
    if (blotter instanceof BmarkHedgeOrdersBlotterComponent) {
      let hedgeBlotter: BmarkHedgeOrdersBlotterComponent =
        blotter as BmarkHedgeOrdersBlotterComponent;
      this.initMenuItems(hedgeBlotter.embeddedMode);
    }

    if (selectedItems) {
      let selectedBenchmarksHedgeOrders = selectedItems as Array<CubiqTS.OrderDataImpl>;
      this.selectedBenchmarksHedgeOrders = selectedBenchmarksHedgeOrders;
      let enabledItems: Array<BfxBlotterAction> = this.getEnabledActions(
        selectedBenchmarksHedgeOrders,
        selectedIds,
        blotter,
      );
      let actionDecorators: any = this.getActionDecorators(selectedBenchmarksHedgeOrders);
      this.showMenuAt(blotter, pageX, pageY, enabledItems, actionDecorators);
    }
  }

  /**
   * Initialize hedge orders blotter context menu items
   *
   * @param embedded true if hedge order blotter is embedded in hedge order monitor, false
   * from benchmark summary
   */
  protected initMenuItems(embedded: boolean = false): void {
    // FIXME Permissions are incorrect from Trade port
    this.acceptItem = new BmarkHedgeOrdersContextMenuItem(
      BfxBlotterAction.ACTION_ORDER_ACCEPT_CANCEL,
      "Accept",
    );
    this.rejectItem = new BmarkHedgeOrdersContextMenuItem(
      BfxBlotterAction.ACTION_ORDER_REJECT_CANCEL,
      "Reject",
    );

    this.pauseMenuItem = new BmarkHedgeOrdersContextMenuItem(
      BfxBlotterAction.ACTION_ORDER_PAUSE,
      "Pause",
    );
    this.resumeMenuItem = new BmarkHedgeOrdersContextMenuItem(
      BfxBlotterAction.ACTION_ORDER_RESUME,
      "Resume",
    );

    this.menuItems = [];

    this.menuItems.push(this.acceptItem);
    this.menuItems.push(this.rejectItem);

    this.menuItems.push(new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));

    this.menuItems.push(this.pauseMenuItem);
    this.menuItems.push(this.resumeMenuItem);

    this.menuItems.push(new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));

    if (this.permissions.canFillNow) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_FILL_NOW, "Fill Now"),
      );
      this.menuItems.push(new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));
    }

    if (this.permissions.canChangeInflightStatus) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(
          BfxBlotterAction.ACTION_ORDER_DISABLE_INFLIGHT,
          "Disable Inflight Checks",
        ),
      );
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(
          BfxBlotterAction.ACTION_ORDER_ENABLE_INFLIGHT,
          "Enable Inflight Checks",
        ),
      );
      this.menuItems.push(new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));
    }

    if (this.permissions.canModifyOrder) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_MODIFY, "Modify"),
      );
    }

    if (this.permissions.canViewTradeDetails) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(
          BfxBlotterAction.ACTION_ORDER_CANCEL,
          "Cancel",
          "icon-menu-error",
        ),
      );
    }

    if (this.permissions.canViewTradeDetails) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_ORDER_UNLOCK, "Unlock"),
      );
    }

    if (this.permissions.canViewDetails) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_TRANSFER, "Transfer"),
      );
    }

    if (this.permissions.canAddFillComment) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_TAKE, "Take"),
      );
    }

    if (this.permissions.canViewAuditLog) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_VIEW_AUDIT, "View Audit Log"),
      );
    }

    if (this.permissions.canViewDetails) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_VIEW_DETAILS, "View Order"),
      );
    }

    if (embedded) {
      if (this.permissions.canRiskRoute && this.permissions.canRiskRouteFromDealt) {
        this.menuItems.push(
          new BmarkHedgeOrdersContextMenuItem(
            BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO_DEALT,
            "Send Residual (Dealt Pair) To ...",
            "long-menu-item",
            "fa icon-ion_triangle_right_filled order__menu__menuItem__icon__vwap_right",
          ),
        );
      }

      if (this.permissions.canRiskRoute) {
        this.menuItems.push(
          new BmarkHedgeOrdersContextMenuItem(
            BfxBlotterAction.ACTION_SPOT_RISK_SEND_TO,
            "Send Residual (Risk Pair) To ...",
            "long-menu-item",
            "fa icon-ion_triangle_right_filled order__menu__menuItem__icon__vwap_right",
          ),
        );
      }
    }

    this.menuItems.push(new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.SEPARATOR));

    if (this.permissions.canExportTrade) {
      this.menuItems.push(
        new BmarkHedgeOrdersContextMenuItem(BfxBlotterAction.ACTION_TRADE_EXPORT, "Export to CSV"),
      );
    }

    this.menuItems.forEach((menuItem) => (menuItem.state.available = true));
    this.menuItems = this.reduceVisibleMenuItems(this.menuItems);
  }
}
